import http from 'src/infrastructure/http'
import { optionsFor } from 'src/services/Helper'
import {
  parseGoodResponse,
  parseFailedResponse,
} from 'src/infrastructure/parse-axios-response'
import {
  BatchWithoutBarcode,
  GenericApiErrors,
  GetOverviewPayload,
  Overview,
  Product,
  GetReceivementWrongProductsPayload,
  WrongProductsOverview,
  GetSalesOverviewPayload,
  SalesOverviewResponse,
  Finish,
  GetFinishPayload,
  PatchBatchObservationPayload,
  PatchItemArObservationPayload,
  PatchResponse,
  CloseSalesAdjustmentPayload,
  ReceivementInvoices,
  GetReceivementInvoicesPayload,
  UploadReceiptsPayload,
  ReleaseReceivementPayload,
  ResendToFinances,
  CheckStatus,
  ReturnInvoice,
  SendJustificationReceiptsPayload,
  GetBatches,
  GetReceivementBatches,
  GetPreviousPendingRequestsPayload,
  PreviousPendingRequestInfo,
  ConfirmTruckDriverPayload,
  GetReceivementInvoicesByReceivementOrRequestPayload,
  ReleaseReceivementSwapReturnPayload,
  ValidadeProductBarcodePayload,
  SignAuthorizationData,
} from './ReceivementTypes'
import { batchMapper } from 'src/utils/mappers/batchMapper'
import { RequestTagInfo } from '../swap-or-return/SwapOrReturnTypes'

const getOrderItems = async (badge: string, receivementId: string | number) => {
  try {
    const result = await http.get(
      `/v2/recebimento/${receivementId}/order-items`,
      optionsFor(badge),
    )
    return parseGoodResponse<Product[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const updateBatchWithoutProduct = async (
  badge: string,
  idBatch: number,
  modelId?: number,
) => {
  try {
    await http.patch(
      `/v2/recebimento/lote/${idBatch}/update-product`,
      {
        model_id: modelId || null,
      },
      optionsFor(badge),
    )
    return { isOk: true }
  } catch (responseFailed) {
    return { isOk: false }
  }
}

export const getBarcodeNotFound = async (
  badge: string,
  receivementId: number | string,
) => {
  try {
    const result = await http.get(
      `/v2/recebimento/${receivementId}/batches-without-barcode`,
      optionsFor(badge),
    )
    return parseGoodResponse<BatchWithoutBarcode[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getOverview = async (payload: GetOverviewPayload) => {
  try {
    const {
      badge, receivementId, onlyDivergents, productDescription, sortBy,
    } = payload

    const result = await http.get(`/v2/recebimento/${receivementId}/overview`, {
      params: {
        sort_by: sortBy || undefined,
        only_divergents: onlyDivergents,
        product_description: productDescription || undefined,
      },
      ...optionsFor(badge),
    })
    return parseGoodResponse<Overview>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getSalesOverview = async (payload: GetSalesOverviewPayload) => {
  try {
    const {
      receivementId, onlyDivergents, productDescription,
    } = payload

    const result = await http.get(`/v2/sales/receivement/${receivementId}/overview`, {
      params: {
        only_divergents: onlyDivergents,
        product_description: productDescription || undefined,
      },
    })

    return parseGoodResponse<SalesOverviewResponse>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const clearUserAssigned = async (payload: GetSalesOverviewPayload) => {
  try {
    const { receivementId } = payload

    const result = await http.patch(`/v2/sales/receivement/${receivementId}/reset`)
    return parseGoodResponse<PatchResponse>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const patchBatchObservation = async (payload: PatchBatchObservationPayload) => {
  try {
    const { batchId, observation } = payload

    const result = await http.patch(`/v2/recebimento/batches/${batchId}`, { observacao: observation.trim() })
    return parseGoodResponse<PatchResponse>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const patchItemArObservation = async (payload: PatchItemArObservationPayload) => {
  try {
    const { itemArId, observation } = payload

    const result = await http.patch(`/v2/recebimento/receivement-authorization-item/${itemArId}`, { observacao: observation.trim() })
    return parseGoodResponse<PatchResponse>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getFinish = async (
  payload: GetFinishPayload,
) => {
  try {
    const {
      badge, receivementId, productDescription,
    } = payload

    const result = await http.get(`/v2/recebimento/${receivementId}/sales-adjustment-result`, {
      params: {
        product_description: productDescription || undefined,
      },
      ...optionsFor(badge),
    })

    return parseGoodResponse<Finish>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getReceivementWrongProducts = async (
  payload: GetReceivementWrongProductsPayload,
) => {
  try {
    const {
      badge, receivementId, productDescription, sortBy,
    } = payload

    const result = await http.get(`/v2/recebimento/${receivementId}/batches/wrong-products`, {
      params: {
        sort_by: sortBy || undefined,
        product_description: productDescription || undefined,
      },
      ...optionsFor(badge),
    })

    result.data.data = result.data.data.map((batch) => batchMapper(batch))

    return parseGoodResponse<WrongProductsOverview>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const closeSalesAdjustment = async (
  payload: CloseSalesAdjustmentPayload,
) => {
  try {
    const { receivementId, data: payloadData } = payload

    const data = payloadData.map((d) => ({
      batch_item_order_ids: d.batchItemOrderIds,
      accepted_quantity: d.acceptedQuantity,
      return_quantity: d.returnQuantity || null,
      swap_quantity: d.swapQuantity || null,
      accept_damage: d.acceptDamage,
      model_id: d.modelId,
      id_ar: d.idReceivementAuthorization,
      quantity_per_box: d.quantityPerBox,
      selected_action: d.option,
    }))

    await http.post(`/v2/recebimento/${receivementId}/close`, { data, purchase_approval: true })

    return parseGoodResponse<{}>({})
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export const getReceivementInvoices = async (
  payload: GetReceivementInvoicesPayload,
) => {
  const { badge, attendancePassword } = payload

  try {
    const result = await http.get(
      '/v2/recebimento/invoices', {
        params: {
          attendance_password: attendancePassword,
          badge,
        },
        ...optionsFor(badge),
      },
    )

    return parseGoodResponse<ReceivementInvoices>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const uploadReceipts = async (payload: UploadReceiptsPayload) => {
  try {
    const {
      pdfFiles, idReceivement,
    } = payload
    const formData = new FormData()
    const filesData = {}
    pdfFiles.forEach((pdfFile) => {
      formData.append(pdfFile.fileName, pdfFile.pdfFile)
      filesData[pdfFile.invoiceKeyReturn] = pdfFile.fileName
    })

    formData.append('receipt_data', JSON.stringify(filesData))
    formData.append('id_receivement', String(idReceivement))

    await http.post('/v2/invoice/receipt-upload', formData)
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const sendJustificationReceipts = async (payload: SendJustificationReceiptsPayload) => {
  try {
    const {
      justification, idReceivement,
    } = payload
    const body = {
      id_receivement: idReceivement,
      justification,
    }
    await http.post('/v2/invoice/receipt-justify', body)
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const resendToFinances = async (
  payload: ResendToFinances,
) => {
  try {
    const { idReceivement, chaveNf } = payload

    const result = await http.post('/v2/recebimento/resend-invoice', { id_receivement: idReceivement, invoice_key: chaveNf })

    return parseGoodResponse<ReceivementInvoices>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const releaseReceivement = async (payload: ReleaseReceivementPayload) => {
  try {
    const { idReceivement } = payload
    await http.post(`/v2/recebimento/${idReceivement}/release`, { badge: payload.badge })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getStatusInvoiceReturn = async (payload: CheckStatus) => {
  try {
    const { idReceivement } = payload

    const result = await http.get(`/v2/receivement/verify-invoices/${idReceivement}`)

    return parseGoodResponse<ReturnInvoice[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export const getReceivementBatches = async (payload: GetBatches) => {
  try {
    const { receivementId, badge } = payload
    const result = await http.get(`/v2/recebimento/${receivementId}/batches`, { ...optionsFor(badge) })

    return parseGoodResponse<GetReceivementBatches>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getPreviousPendingRequests = async (payload: GetPreviousPendingRequestsPayload) => {
  const { idSupplier, idFulfillmentCenter } = payload
  try {
    const result = await http.get(`/v2/recebimento/previous-pending-requests/${idSupplier}/${idFulfillmentCenter}`)
    return parseGoodResponse<PreviousPendingRequestInfo>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const confirmTruckDriver = async (payload: ConfirmTruckDriverPayload) => {
  try {
    const { idReceivement, isAvailable } = payload
    const body = {
      id_receivement: idReceivement,
      is_available: isAvailable,
    }
    const result = await http.post('/v2/recebimento/release/confirm-truck-driver', body)
    return parseGoodResponse<RequestTagInfo[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export const getReceivementInvoicesByReceivementOrRequest = async (
  payload: GetReceivementInvoicesByReceivementOrRequestPayload,
) => {
  const { idReceivement, idRequest } = payload

  try {
    const result = await http.get(
      '/v2/recebimento/invoices/by-receivement-or-request', {
        params: {
          id_receivement: idReceivement,
          id_request: idRequest,
        },
      },
    )

    return parseGoodResponse<ReceivementInvoices>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const releaseReceivementSwapReturn = async (payload: ReleaseReceivementSwapReturnPayload) => {
  try {
    const { idReceivement } = payload
    await http.post(`/v2/recebimento/${idReceivement}/release/swap-return`)
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const validateProductBarcode = async (payload: ValidadeProductBarcodePayload) => {
  try {
    const { barcode, idModel } = payload
    const body = {
      barcode,
      id_model: idModel,
    }

    const result = await http.post('/v2/product/validate/barcode', body)
    return parseGoodResponse<any>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getOpenedInvoicesReturn = async (idReceivement: number) => {
  try {
    const result = await http.get(`/v2/receivement/invoices/${idReceivement}`)

    return parseGoodResponse<ReturnInvoice[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const signAuthorization = async (accessKey: string) => {
  try {
    const response = await http.post('/v2/recebimento/authorization/sign', { access_key: accessKey })
    return parseGoodResponse<SignAuthorizationData>(response.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export default {
  getOrderItems,
  updateBatchWithoutProduct,
  getBarcodeNotFound,
  getOverview,
  getSalesOverview,
  getReceivementWrongProducts,
  getFinish,
  patchBatchObservation,
  patchItemArObservation,
  closeSalesAdjustment,
  getReceivementInvoices,
  uploadReceipts,
  resendToFinances,
  getStatusInvoiceReturn,
  clearUserAssigned,
  releaseReceivement,
  sendJustificationReceipts,
  getReceivementBatches,
  getPreviousPendingRequests,
  confirmTruckDriver,
  getReceivementInvoicesByReceivementOrRequest,
  releaseReceivementSwapReturn,
  validateProductBarcode,
  getOpenedInvoicesReturn,
  signAuthorization,
}
